import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider';
import { getMyVault, renameVault } from '../lib/vault/vaultApi';
import { PushToggle } from './PushToggle';
import './vault.css';

/**
 * "You" — the account surface. Identity, the one thing a member can name (their
 * vault), and the way out. Sign-out existed in the auth layer but had no button;
 * vault rename existed on the backend but was mounted in no module (a 404) and
 * had no UI. Both are wired here.
 */
export default function YouScreen() {
  const { user, logout } = useAuth();
  const { api } = useAuth();
  const [vaultId, setVaultId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [savedName, setSavedName] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const dash = await getMyVault(api);
        setVaultId(dash.vault.id);
        setName(dash.vault.name);
        setSavedName(dash.vault.name);
      } catch {
        // No vault yet — the rename card simply won't render.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function save() {
    if (vaultId === null || name.trim().length === 0 || name.trim() === savedName) return;
    setBusy(true); setError(''); setNotice('');
    try {
      await renameVault(api, vaultId, name.trim());
      setSavedName(name.trim());
      setNotice('Saved.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not rename your vault.');
    } finally { setBusy(false); }
  }

  return (
    <section className="vault">
      <p className="vault__kicker">Your account</p>
      <h1 className="vault__title">You</h1>
      {user?.email && <p className="vault__sub">Signed in as {user.email}</p>}

      {vaultId !== null && (
        <div className="la-card" style={{ marginTop: 24, padding: 24 }}>
          <p className="vault__section-label">Vault name</p>
          <label className="auth__field">
            <input className="auth__input" value={name} onChange={(e) => { setName(e.target.value); setNotice(''); }} aria-label="Vault name" />
          </label>
          {error && <p className="auth__alert" role="alert">{error}</p>}
          {notice && <p className="auth__notice" role="status">{notice}</p>}
          <button className="la-btn-secondary" onClick={() => void save()} disabled={busy || name.trim().length === 0 || name.trim() === savedName}>
            {busy ? 'Saving…' : 'Save name'}
          </button>
        </div>
      )}

      <div className="la-card" style={{ marginTop: 24, padding: 24 }}>
        <p className="vault__section-label">If you are not here</p>
        <p className="vault__sub" style={{ marginTop: 8 }}>
          The people who can act when you cannot. Your guardians confirm together; your executor is
          the single person you trust to authorize the release on their own.
        </p>
        <div className="la-cta-wrap" style={{ marginTop: 16 }}>
          <Link className="la-btn-secondary" to="/guardians">Guardians</Link>
        </div>
        <div className="la-cta-wrap" style={{ marginTop: 12 }}>
          <Link className="la-btn-secondary" to="/executors">Executor</Link>
        </div>
      </div>

      <div className="la-card" style={{ marginTop: 24, padding: 24 }}>
        <p className="vault__section-label">For someone who can’t use the app</p>
        <p className="vault__sub" style={{ marginTop: 8 }}>
          Approve recording links for a grandparent or family member, and see messages they’ve
          recorded that are waiting for your review.
        </p>
        <div className="la-cta-wrap" style={{ marginTop: 16 }}>
          <Link className="la-btn-secondary" to="/recordings">Recordings</Link>
        </div>
      </div>

      <div className="you__block">
        <p className="vault__section-label">What came back</p>
        <p className="vault__sub" style={{ marginTop: 8 }}>
          Read the replies people have written back to the messages you left them.
        </p>
        <div className="la-cta-wrap" style={{ marginTop: 16 }}>
          <Link className="la-btn-secondary" to="/replies">Replies</Link>
        </div>
      </div>

      <div className="la-card" style={{ marginTop: 24, padding: 24 }}>
        <p className="vault__section-label">If you lose your phone</p>
        <p className="vault__sub" style={{ marginTop: 8 }}>
          Your vault is meant to last decades; phones do not. A Recovery Kit is twenty-four
          characters that get you back in on your own, straight away. Without one, getting back in
          takes three days.
        </p>
        <div className="la-cta-wrap" style={{ marginTop: 16 }}>
          <Link className="la-btn-secondary" to="/you/recovery-kit">Recovery Kit</Link>
        </div>
      </div>

      <PushToggle />

      <div className="la-cta-wrap" style={{ marginTop: 28 }}>
        <button className="la-btn-secondary" onClick={() => logout()}>Sign out</button>
      </div>
    </section>
  );
}
